package com.castly.shared.dto

import kotlinx.serialization.Serializable

@Serializable
data class PodcastTranslationData(
    val title: String,
    val description: String? = null,
    val author: String? = null
)

@Serializable
data class PodcastDto(
    val id: String,
    val title: String,
    val slug: String,
    val description: String? = null,
    val author: String? = null,
    val coverArtUrl: String? = null,
    val originalCoverArtUrl: String? = null,
    val language: String = "en",
    val explicit: Boolean = false,
    val createdAt: String,
    val updatedAt: String,
    val translations: Map<String, PodcastTranslationData> = emptyMap(),
    // Linked podcast fields
    val rssUrl: String? = null,
    val isLinked: Boolean = false,
    val lastSyncedAt: String? = null,
    val syncEnabled: Boolean = true,
    val syncIntervalMinutes: Int = 60,
    val syncError: String? = null,
    // Episode count
    val episodeCount: Int? = null,
    // Type classification
    val type: String = "PODCAST",
    // Tags
    val tags: List<TagDto> = emptyList(),
    // RSS metadata fields
    val location: String? = null,
    val rating: String? = null,
    val copyright: String? = null,
    val subtitle: String? = null,
    val ownerEmail: String? = null,
    val ownerName: String? = null,
    // Soft-delete
    val deletedAt: String? = null
)

@Serializable
data class CreatePodcastRequest(
    val title: String,
    val description: String? = null,
    val author: String? = null,
    val coverArtUrl: String? = null,
    val language: String = "en",
    val explicit: Boolean = false,
    val translations: Map<String, PodcastTranslationData> = emptyMap(),
    val type: String? = null,
    val location: String? = null,
    val rating: String? = null,
    val copyright: String? = null,
    val subtitle: String? = null,
    val ownerEmail: String? = null,
    val ownerName: String? = null
)

@Serializable
data class UpdatePodcastRequest(
    val title: String? = null,
    val description: String? = null,
    val author: String? = null,
    val coverArtUrl: String? = null,
    val language: String? = null,
    val explicit: Boolean? = null,
    val translations: Map<String, PodcastTranslationData>? = null,
    val type: String? = null,
    val location: String? = null,
    val rating: String? = null,
    val copyright: String? = null,
    val subtitle: String? = null,
    val ownerEmail: String? = null,
    val ownerName: String? = null
)

@Serializable
data class PodcastListResponse(
    val items: List<PodcastDto>,
    val total: Int,
    val page: Int,
    val pageSize: Int
)

// Linked Podcast DTOs
@Serializable
data class RssPreviewRequest(
    val rssUrl: String,
    val followPagination: Boolean = true,
    val maxPages: Int = 50,
    val maxEpisodes: Int = 5000
)

@Serializable
data class RssPreviewResponse(
    val title: String,
    val description: String? = null,
    val author: String? = null,
    val coverArtUrl: String? = null,
    val language: String = "en",
    val explicit: Boolean = false,
    val episodeCount: Int,
    val rssUrl: String,
    val pagesFetched: Int = 1,
    val type: String = "PODCAST"
)

@Serializable
data class ImportRssRequest(
    val rssUrl: String,
    val followPagination: Boolean = true,
    val maxPages: Int = 50,
    val maxEpisodes: Int = 5000,
    val type: String? = null
)

@Serializable
data class SyncSettingsRequest(
    val syncEnabled: Boolean? = null,
    val syncIntervalMinutes: Int? = null
)

@Serializable
data class SyncResultResponse(
    val podcastId: String,
    val newEpisodes: Int,
    val updatedEpisodes: Int,
    val errors: List<String> = emptyList()
)

@Serializable
data class PodcastTypeWithCountDto(
    val type: String,
    val displayName: String,
    val podcastCount: Int
)

// Bulk Import DTOs
@Serializable
data class BulkRssPreviewRequest(
    val rssUrls: List<String>,
    val followPagination: Boolean = true,
    val maxPages: Int = 50,
    val maxEpisodes: Int = 5000
)

@Serializable
data class RssPreviewResult(
    val rssUrl: String,
    val status: PreviewStatus,
    val previews: List<RssPreviewResponse>? = null,
    val error: String? = null
)

@Serializable
enum class PreviewStatus { SUCCESS, ERROR }

@Serializable
data class BulkRssPreviewResponse(
    val results: List<RssPreviewResult>,
    val totalRequested: Int,
    val successCount: Int,
    val errorCount: Int
)

@Serializable
data class BulkRssImportRequest(
    val imports: List<SingleImportRequest>,
    val followPagination: Boolean = true,
    val maxPages: Int = 50,
    val maxEpisodes: Int = 5000
)

@Serializable
data class SingleImportRequest(
    val rssUrl: String,
    val type: String? = null
)

@Serializable
data class RssImportResult(
    val rssUrl: String,
    val status: ImportStatus,
    val podcasts: List<PodcastDto>? = null,
    val error: String? = null
)

@Serializable
enum class ImportStatus { SUCCESS, SKIPPED_DUPLICATE, ERROR }

@Serializable
data class BulkRssImportResponse(
    val results: List<RssImportResult>,
    val totalRequested: Int,
    val successCount: Int,
    val duplicateCount: Int,
    val errorCount: Int
)
